import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Link as MuiLink,
  Divider
} from '@mui/material';
import { Helmet } from 'react-helmet-async';
import { SITE_NAME } from '../constants/site';
import { getReleaseNotes } from '../services/api';
import { ReleaseNote } from '../types/ReleaseNote';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { useNavigate } from 'react-router-dom';

const ReleaseNotesPage: React.FC = () => {
  const [releaseNotes, setReleaseNotes] = useState<ReleaseNote[]>([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchReleaseNotes = async () => {
      try {
        const data = await getReleaseNotes();
        setReleaseNotes(data);
      } catch (error) {
        console.error('リリースノートの取得に失敗しました:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchReleaseNotes();
  }, []);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('ja-JP', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const handleLinkClick = (note: ReleaseNote) => {
    if (note.link_url) {
      if (note.link_url.startsWith('http')) {
        window.open(note.link_url, '_blank', 'noopener noreferrer');
      } else {
        navigate(note.link_url);
      }
    }
  };

  return (
    <>
      <Helmet>
        <title>リリースノート | {SITE_NAME}</title>
      </Helmet>

      <Container maxWidth="md" sx={{ py: 4 }}>
        <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 48,
              height: 48,
              borderRadius: '12px',
              background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.2) 0%, rgba(118, 75, 162, 0.2) 100%)',
            }}
          >
            <AutoAwesomeIcon sx={{ color: '#667eea', fontSize: '1.5rem' }} />
          </Box>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 600 }}>
            リリースノート
          </Typography>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : releaseNotes.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <Typography variant="h6" color="text.secondary">
              リリースノートはまだありません
            </Typography>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {releaseNotes.map((note) => (
              <Card
                key={note.id}
                sx={{
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                  background: 'rgba(255, 255, 255, 0.02)',
                  '&:hover': {
                    transform: 'none'
                  }
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                    <Typography variant="h5" sx={{ fontWeight: 600 }}>
                      {note.title}
                    </Typography>
                    <Chip
                      label="NEW"
                      size="small"
                      color="primary"
                      sx={{
                        height: 20,
                        fontSize: '0.7rem',
                        fontWeight: 600
                      }}
                    />
                    <Chip
                      label={note.version}
                      size="small"
                      variant="outlined"
                      sx={{
                        height: 20,
                        fontSize: '0.7rem',
                        fontWeight: 500,
                        borderColor: 'rgba(255, 255, 255, 0.2)',
                        color: 'text.secondary'
                      }}
                    />
                  </Box>

                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 2 }}
                  >
                    {formatDate(note.created_at)}
                  </Typography>

                  <Divider sx={{ my: 2 }} />

                  <Typography
                    variant="body1"
                    sx={{
                      color: 'text.primary',
                      lineHeight: 1.7,
                      whiteSpace: 'pre-wrap',
                      mb: note.link_text ? 2 : 0
                    }}
                  >
                    {note.description}
                  </Typography>

                  {note.link_text && (
                    <MuiLink
                      component="button"
                      variant="body2"
                      onClick={() => handleLinkClick(note)}
                      sx={{
                        color: 'primary.main',
                        textDecoration: 'none',
                        fontWeight: 500,
                        '&:hover': {
                          textDecoration: 'underline'
                        }
                      }}
                    >
                      {note.link_text} →
                    </MuiLink>
                  )}
                </CardContent>
              </Card>
            ))}
          </Box>
        )}
      </Container>
    </>
  );
};

export default ReleaseNotesPage;
